/**
 * Simple event emitter for cross-component communication
 * Used to communicate between components without passing props or using Vuex
 */

type EventHandler = (...args: any[]) => void;

class EventEmitter {
  private events: Record<string, EventHandler[]> = {};

  /**
   * Register an event handler
   * @param event Event name
   * @param handler Handler function
   */
  on(event: string, handler: EventHandler): void {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(handler);
  }

  /**
   * Remove an event handler
   * @param event Event name
   * @param handler Handler function to remove (if not provided, all handlers for the event are removed)
   */
  off(event: string, handler?: EventHandler): void {
    if (!this.events[event]) return;
    
    if (!handler) {
      // Remove all handlers for this event
      delete this.events[event];
      return;
    }
    
    // Remove specific handler
    this.events[event] = this.events[event].filter(h => h !== handler);
  }

  /**
   * Emit an event with data
   * @param event Event name
   * @param args Arguments to pass to handlers
   */
  emit(event: string, ...args: any[]): void {
    if (!this.events[event]) return;
    
    this.events[event].forEach(handler => {
      try {
        handler(...args);
      } catch (error) {
        console.error(`Error in event handler for ${event}:`, error);
      }
    });
  }
}

// Create a singleton instance
const emitter = new EventEmitter();

export default emitter; 